import { api, showErrorNotification, showSuccessNotification } from '@med-fe/util';
import { all, call, put, takeLatest, select } from 'redux-saga/effects';
import {
  FETCH_PERMISSIONS_LIST,
  FETCH_PERMISSION_MAPPINGS,
  FETCH_ROLE_PERMISSIONS,
  FETCH_ROLE_USERS,
  FETCH_ROLES,
  fetchPermissionsListFailure,
  fetchPermissionsListSuccess,
  fetchRolePermissionsFailure,
  fetchRolePermissionsSuccess,
  fetchRolesFailure,
  fetchRolesSuccess,
  fetchRoleUsersFailure,
  fetchRoleUsersSuccess,
  fetchPermissionMappingsFailure,
  fetchPermissionMappingsSuccess,
  MAP_ROLE_PERMISSION,
  mapRolePermissionFailure,
  mapRolePermissionSuccess,
  UNMAP_ROLE_PERMISSION,
  unmapRolePermissionFailure,
  unmapRolePermissionSuccess,
  MAP_ROLE_USER,
  mapRoleUserSuccess,
  mapRoleUserFailure,
  UNMAP_ROLE_USER,
  unmapRoleUserSuccess,
  unmapRoleUserFailure,
  MAP_ROLE_ADUSER,
  mapRoleAdUserSuccess,
  mapRoleAdUserFailure,
  UNMAP_ROLE_ADUSER,
  unmapRoleAdUserSuccess,
  unmapRoleAdUserFailure,
  ADD_ROLE,
  addRoleSuccess,
  addRoleFailure,
} from './actions';
import { USER_ROLE_CONFIG_STATE_KEY } from './reducer';

// workers
export function* fetchRolesWorker() {
  try {
    const resp = yield call(api.get, '/userroleconfiguration/userroles', true, true);
    yield put(fetchRolesSuccess(resp || []));
  } catch (err) {
    yield put(fetchRolesFailure(err));
  }
}

export function* fetchPermissionsListWorker() {
  try {
    const resp = yield call(api.get, '/userroleconfiguration/permissions', true, true);
    yield put(fetchPermissionsListSuccess(resp || []));
  } catch (err) {
    yield put(fetchPermissionsListFailure(err));
  }
}

export function* fetchRolePermissionsWorker(action: any) {
  try {
    const roleId: number = action.payload;
    const resp = yield call(api.get, `/userroleconfiguration/userroles/${roleId}/permissions`, true, true);
    yield put(fetchRolePermissionsSuccess(roleId, resp || []));
  } catch (err) {
    yield put(fetchRolePermissionsFailure(err));
  }
}

// removed fetchAdUsersWorker - AD users list is not used anymore

export function* fetchRoleUsersWorker(action: any) {
  try {
    const roleId: number = action.payload;
    const resp = yield call(api.get, `/userroleconfiguration/userroles/${roleId}/users`, true, true);
    yield put(fetchRoleUsersSuccess(roleId, resp || []));
  } catch (err) {
    yield put(fetchRoleUsersFailure(err));
  }
}

export function* mapRolePermissionWorker(action: any) {
  try {
    const { userRoleId, permissionId } = action.payload;
    // POST /userroleconfiguration/userroles/map { userRoleId, permissionId }
    const resp = yield call(
      api.post,
      '/userroleconfiguration/userroles/mappermission',
      { userRoleId, permissionId },
      null,
      true,
      true
    );
    yield put(mapRolePermissionSuccess(userRoleId, permissionId));
    // refresh role permissions
    yield put({ type: FETCH_ROLE_PERMISSIONS, payload: userRoleId });
  } catch (err) {
    yield put(mapRolePermissionFailure(err));
  }
}

export function* unmapRolePermissionWorker(action: any) {
  try {
    const { userRoleId, permissionId } = action.payload;
    // DELETE with body (axios supports data in config)
    const resp = yield call(
      api.post,
      '/userroleconfiguration/userroles/unmappermission',
      { userRoleId, permissionId },
      null,
      true,
      true
    );
    yield put(unmapRolePermissionSuccess(userRoleId, permissionId));
    // refresh role permissions
    yield put({ type: FETCH_ROLE_PERMISSIONS, payload: userRoleId });
  } catch (err) {
    yield put(unmapRolePermissionFailure(err));
  }
}

export function* mapRoleAdUserWorker(action: any) {
  try {
    const { userRoleId, adUserId } = action.payload;
    // POST map { userRoleId, emial } -- backend expects `emial` field for email
    yield call(api.post, '/userroleconfiguration/userroles/mapuser', { userRoleId, email: adUserId }, null, true, true);
    yield put(mapRoleAdUserSuccess(userRoleId, adUserId));
    // refresh mapped users for the role
    yield put({ type: FETCH_ROLE_USERS, payload: userRoleId });
    // Show success notification with role display name if available
    try {
      const roles = yield select((state: any) => state[USER_ROLE_CONFIG_STATE_KEY]?.roles || []);
      const role = (roles || []).find((r: any) => Number(r.Id) === Number(userRoleId));
      showSuccessNotification(`User ${adUserId} added to role ${role?.DisplayName || userRoleId} successfully`);
    } catch (e) {
      showSuccessNotification(`User ${adUserId} added to role ${userRoleId} successfully`);
    }
  } catch (err: any) {
    yield put(mapRoleAdUserFailure(err));
    const apiData = err?.response?.data || {};
    const message =
      apiData?.errors?.[0] ||
      apiData?.error?.messages?.[0] ||
      apiData?.message ||
      (typeof apiData === 'string' ? apiData : null) ||
      err?.message ||
      `Error while adding user ${action.payload?.adUserId}. Please try again.`;
    showErrorNotification(message);
  }
}

export function* unmapRoleAdUserWorker(action: any) {
  try {
    const { userRoleId, adUserId } = action.payload;
    // POST unmap { userRoleId, emial } (backend expects `emial` for email)
    yield call(
      api.post,
      '/userroleconfiguration/userroles/unmapuser',
      { userRoleId, email: adUserId },
      null,
      true,
      true
    );
    yield put(unmapRoleAdUserSuccess(userRoleId, adUserId));
    // refresh mapped users for the role
    yield put({ type: FETCH_ROLE_USERS, payload: userRoleId });
    try {
      const roles = yield select((state: any) => state[USER_ROLE_CONFIG_STATE_KEY]?.roles || []);
      const role = (roles || []).find((r: any) => Number(r.Id) === Number(userRoleId));
      showSuccessNotification(`User ${adUserId} removed from role ${role?.DisplayName || userRoleId} successfully`);
    } catch (e) {
      showSuccessNotification(`User ${adUserId} removed from role ${userRoleId} successfully`);
    }
  } catch (err: any) {
    yield put(unmapRoleAdUserFailure(err));
    const apiData = err?.response?.data || {};
    const message =
      apiData?.errors?.[0] ||
      apiData?.error?.messages?.[0] ||
      apiData?.message ||
      (typeof apiData === 'string' ? apiData : null) ||
      err?.message ||
      `Error while removing user ${action.payload?.adUserId}. Please try again.`;
    showErrorNotification(message);
  }
}

export function* addRoleWorker(action: any) {
  try {
    const body = action.payload;
    const resp = yield call(api.post, '/userroleconfiguration/userroles', body, null, true, true);
    yield put(addRoleSuccess(resp));
    // refresh roles list
    yield put({ type: FETCH_ROLES });
  } catch (err) {
    yield put(addRoleFailure(err));
  }
}

export function* fetchPermissionMappingsWorker(action: any) {
  try {
    const permissionId: number = action.payload;
    const resp = yield call(api.get, `/userroleconfiguration/permissions/${permissionId}/users`, true, true);
    yield put(fetchPermissionMappingsSuccess(permissionId, resp || { Roles: [], Users: [] }));
  } catch (err) {
    yield put(fetchPermissionMappingsFailure(err));
  }
}
